import { TFunction } from 'i18next';
import {
  CritiqueBookRef,
  CritiqueCheckpoint,
  CritiqueFeedbackView,
  CritiqueGroup,
  CritiqueGroupVisibility,
  CritiqueMember,
  CritiqueMemberRole,
  CritiqueUserRef,
} from 'src/hooks/useCritiqueGroups';

/**
 * Pure helpers for the Critique Groups UI. Everything here is a function of
 * data the server already returned — deliberately so, because the interesting
 * rules (who may see whose feedback) are enforced server-side and must not be
 * re-derived optimistically in the client.
 */

/**
 * The free "taste" limits, mirrored from the server's FREE_TASTE
 * (api/src/utils/groups/critiqueGroupTier.js). Used ONLY to explain the limits
 * in copy and to pre-empt a doomed request — the server is what enforces them,
 * and it also silently CLAMPS `memberCap` rather than erroring. Never treat
 * these as authoritative; always handle UPGRADE_REQUIRED_* coming back anyway.
 */
export const FREE_TASTE = Object.freeze({
  maxGroups: 1,
  memberCap: 5,
  maxBooks: 1,
});

// ── Visibility (private / link-only / public) ───────────────────────────────

/**
 * The Semantic UI icon + label for a group's visibility. PRESENTATIONAL ONLY —
 * whether someone may actually join is enforced server-side; this only picks
 * how to draw the badge. The three cases are exhaustive over
 * `CritiqueGroupVisibility`; an unknown value falls back to the private look
 * (the safe direction — never advertise a group as public unless it is).
 */
export const visibilityLabel = (
  visibility: CritiqueGroupVisibility | undefined,
  t: TFunction
): { icon: string; label: string } => {
  switch (visibility) {
    case 'listed':
      return { icon: 'globe', label: t('CritiquePublic', 'Public') };
    case 'request':
      return { icon: 'linkify', label: t('CritiqueLinkOnly', 'Link-only') };
    case 'invite_only':
    default:
      return { icon: 'lock', label: t('CritiquePrivate', 'Private') };
  }
};

/** True for the visibilities that expose a group beyond its membership. */
export const isPublicVisibility = (
  visibility: CritiqueGroupVisibility | undefined
): boolean => visibility === 'request' || visibility === 'listed';

// ── Identity / roles ────────────────────────────────────────────────────────

export const userRefId = (user: CritiqueUserRef | string | undefined): string =>
  typeof user === 'string' ? user : user?._id || '';

/**
 * A member's display label.
 *
 * When a profile has no `displayName` the raw ref is only a 24-char ObjectId,
 * and every call site used to fall back to that id — an ugly, unbreakable
 * "word" that also inflated the page's min-content width (see memberName.tsx).
 *
 * Pass `t` to get a FRIENDLY fallback instead: a short, stable "Reader ####"
 * derived from the last four hex of the id. It reads like a name, stays within
 * a phone viewport, and — unlike a flat "A member" — keeps members
 * distinguishable from one another in a roster. Without `t` the behaviour is
 * unchanged (raw name or ''), so existing `|| userRefId(...)` call sites keep
 * working until they opt in.
 */
export const userRefName = (
  user: CritiqueUserRef | string | undefined,
  t?: TFunction
): string => {
  const name = typeof user === 'string' ? '' : user?.displayName?.trim() || '';
  if (name) return name;
  if (!t) return '';
  const id = userRefId(user);
  const suffix = id.slice(-4);
  return suffix
    ? t('CritiqueReaderShort', 'Reader {{suffix}}', { suffix })
    : t('CritiqueAMember', 'A member');
};

/**
 * `group.books` is populated to {_id, title} by GET /:groupId but is a bare id
 * elsewhere — same shape problem as the user refs above, same treatment.
 */
export const bookRefId = (book: CritiqueBookRef | string | undefined): string =>
  typeof book === 'string' ? book : book?._id || '';

export const bookRefTitle = (
  book: CritiqueBookRef | string | undefined
): string => (typeof book === 'string' ? '' : book?.title || '');

// ── Cover art ───────────────────────────────────────────────────────────────

/**
 * The url of a linked book's OWN cover art, or undefined when the book has none
 * (or the ref isn't populated). Prefers the thumbnail — a card renders small, so
 * the lighter image is enough and cheaper — and falls back to the high-res.
 */
export const bookRefCoverUrl = (
  book: CritiqueBookRef | string | undefined
): string | undefined => {
  if (!book || typeof book === 'string') return undefined;
  const cover = book.cover;
  if (!cover || typeof cover === 'string') return undefined;
  return cover.thumbnail?.url || cover.highRes?.url || undefined;
};

/** The url of the group's own uploaded cover graphic, or undefined when unset. */
export const groupCoverUrl = (
  group: CritiqueGroup | undefined
): string | undefined => {
  const image = group?.coverImage;
  if (!image || typeof image === 'string') return undefined;
  return image.url || undefined;
};

/**
 * The cover to show for a group, in priority order:
 *   1. the host's uploaded group cover,
 *   2. the linked book's own cover,
 *   3. undefined → the caller renders the tinted default spine.
 * This is the single source of truth for the group card/header cover so every
 * screen agrees on what "the cover" is.
 */
export const groupSpineCoverUrl = (
  group: CritiqueGroup | undefined
): string | undefined =>
  groupCoverUrl(group) || bookRefCoverUrl((group?.books || [])[0]);

/** Initials for an avatar chip. Falls back to '?' rather than rendering blank. */
export const getInitials = (name?: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

/**
 * Deterministic avatar colour from a uid, so a member keeps the same colour
 * across screens without the server storing one.
 */
export const avatarColor = (id: string): string => {
  const palette = [
    '#66cc88',
    '#5588aa',
    '#bb6677',
    '#77aa99',
    '#aa7799',
    '#d98b46',
    '#7788cc',
    '#4faa9c',
  ];
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
};

export const findMembership = (
  group: CritiqueGroup | undefined,
  uid: string | undefined
): CritiqueMember | undefined => {
  if (!group?.members || !uid) return undefined;
  return group.members.find(m => userRefId(m.user) === uid);
};

export const getMyRole = (
  group: CritiqueGroup | undefined,
  uid: string | undefined
): CritiqueMemberRole | undefined => findMembership(group, uid)?.role;

/**
 * The host is the group's creator. `createdBy` is authoritative and always
 * present — unlike `members`, which is only populated on some responses.
 */
export const isHost = (
  group: CritiqueGroup | undefined,
  uid: string | undefined
): boolean => !!group && !!uid && group.createdBy === uid;

/** Host or moderator: may manage checkpoints, pin posts, resolve requests. */
export const canManageGroup = (
  group: CritiqueGroup | undefined,
  uid: string | undefined
): boolean => {
  const role = getMyRole(group, uid);
  return role === 'admin' || role === 'moderator';
};

/** Only the host (admin) may change feedback settings or manage moderators. */
export const canEditSettings = (
  group: CritiqueGroup | undefined,
  uid: string | undefined
): boolean => getMyRole(group, uid) === 'admin' || isHost(group, uid);

/** Members who consume a cap seat. The host's `admin` seat does not count. */
export const activeMembers = (group: CritiqueGroup | undefined): CritiqueMember[] =>
  (group?.members || []).filter(
    m => m.role === 'member' || m.role === 'moderator'
  );

// ── Checkpoints ─────────────────────────────────────────────────────────────

export type CheckpointState =
  | 'completed'
  | 'overdue'
  | 'due_today'
  | 'due_soon'
  | 'upcoming';

/** A checkpoint is "due soon" from this many days out. */
export const DUE_SOON_DAYS = 3;

const startOfDay = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** Whole days from `now` to `dueAt`, by calendar day (0 = due today). */
export const daysUntil = (dueAt: string | Date, now: Date = new Date()): number => {
  const due = typeof dueAt === 'string' ? new Date(dueAt) : dueAt;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((startOfDay(due) - startOfDay(now)) / MS_PER_DAY);
};

/**
 * The visual state of a checkpoint.
 *
 * `done` wins over every date state: a met checkpoint reads "completed" even if
 * its date has passed, so a member who finished late is never shown as overdue.
 * A guiding checkpoint (`deliverable.required === false`) is never overdue —
 * the server treats it as always done, and nagging about it would contradict
 * that. Compares by calendar day, so "due today" holds all day locally.
 */
export const getCheckpointState = (
  checkpoint: Pick<CritiqueCheckpoint, 'dueAt' | 'deliverable'>,
  options: { done?: boolean; now?: Date } = {}
): CheckpointState => {
  const { done = false, now = new Date() } = options;
  if (done) return 'completed';

  const days = daysUntil(checkpoint.dueAt, now);
  const isGuiding = checkpoint.deliverable?.required === false;

  if (days < 0) return isGuiding ? 'upcoming' : 'overdue';
  if (days === 0) return 'due_today';
  if (days <= DUE_SOON_DAYS) return 'due_soon';
  return 'upcoming';
};

/** Ascending by date — the timeline reads as a journey through the book. */
export const sortCheckpoints = (
  checkpoints: CritiqueCheckpoint[]
): CritiqueCheckpoint[] =>
  [...(checkpoints || [])].sort(
    (a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
  );

/** The next checkpoint still ahead (or today), for the header/group card. */
export const nextCheckpoint = (
  checkpoints: CritiqueCheckpoint[],
  now: Date = new Date()
): CritiqueCheckpoint | undefined =>
  sortCheckpoints(checkpoints).find(c => daysUntil(c.dueAt, now) >= 0);

/** Only custom deliverables are self-markable (the API 400s on the rest). */
export const isSelfMarkable = (checkpoint: CritiqueCheckpoint): boolean =>
  checkpoint.deliverable?.type === 'custom';

/** `required !== false` blocks "done" — mirrors the server's blocksDone(). */
export const isRequired = (checkpoint: CritiqueCheckpoint): boolean =>
  !checkpoint.deliverable || checkpoint.deliverable.required !== false;

// ── Feedback visibility (the anti-anchoring core) ───────────────────────────

export type FeedbackLockState =
  /** host_only: members never see each other's feedback. Not a lock to open. */
  | 'host_only'
  /** No gate applies — host view, or members_shared + immediate. */
  | 'open'
  /** after_own: the member has contributed to this part, so it's revealed. */
  | 'unlocked'
  /** after_own: the member hasn't contributed to this part yet. */
  | 'locked';

/**
 * Whether this part's other-member feedback is revealed to the requester.
 *
 * Reads ONLY what the server sent. `unlockedParts` is present exactly when the
 * after_own gate applies and lists the requester's OWN contributed parts; its
 * absence means no gate. We never infer a lock from the feedback arrays, and we
 * never infer that hidden feedback exists — locked items are filtered out
 * server-side with no trace, so "locked" and "nobody commented" look identical
 * on the wire and must not be reported as if they were distinguishable.
 */
export const getFeedbackLockState = (
  view: CritiqueFeedbackView | undefined,
  partId: string | undefined
): FeedbackLockState => {
  if (!view) return 'locked'; // Fail closed while unknown.
  if (view.feedbackVisibility === 'host_only') return 'host_only';
  if (!view.unlockedParts) return 'open';
  if (!partId) return 'locked'; // No part → can't prove contribution → closed.
  return view.unlockedParts.includes(partId) ? 'unlocked' : 'locked';
};

/** True when the reveal is something the member can act on by contributing. */
export const isUnlockable = (state: FeedbackLockState): boolean =>
  state === 'locked';

export const commentsForPart = (
  view: CritiqueFeedbackView | undefined,
  partId: string
) => (view?.comments || []).filter(c => (c.part || '').toString() === partId);

export const surveyAnswersForPart = (
  view: CritiqueFeedbackView | undefined,
  partId: string
) =>
  (view?.surveyAnswers || []).filter(
    a => (a.survey || '').toString() === partId
  );

// NOTE: there is deliberately no "how many others left feedback here" helper.
// The locked state must not hint at the volume behind it — see the lock copy in
// components/critiqueGroups/feedbackReveal.tsx and §12.12 of the plan.
